/**
 * JWT Utility Functions
 * Handles JWT token parsing and validation
 */

/**
 * Decode JWT token without verification (for client-side use)
 * Note: This is for reading token payload only, not for security validation
 */
export const decodeJWT = (token) => {
  try {
    if (!token) return null;
    
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    
    const payload = parts[1];
    const decoded = JSON.parse(atob(payload));
    
    return decoded;
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
};

/**
 * Check if JWT token is expired
 */
export const isTokenExpired = (token) => {
  try {
    const decoded = decodeJWT(token);
    if (!decoded || !decoded.exp) return true;
    
    const currentTime = Date.now() / 1000;
    return decoded.exp < currentTime;
  } catch (error) {
    return true;
  }
};

/**
 * Extract user permissions from JWT token
 */
export const getPermissionsFromToken = (token) => {
  try {
    const decoded = decodeJWT(token);
    return decoded?.permissions || [];
  } catch (error) {
    console.error('Error extracting permissions from token:', error);
    return [];
  }
};

/**
 * Extract user role from JWT token
 */
export const getRoleFromToken = (token) => {
  try {
    const decoded = decodeJWT(token);
    return decoded?.role || null;
  } catch (error) {
    console.error('Error extracting role from token:', error);
    return null;
  }
};

/**
 * Extract user ID from JWT token
 */
export const getUserIdFromToken = (token) => {
  try {
    const decoded = decodeJWT(token);
    return decoded?.id || null;
  } catch (error) {
    console.error('Error extracting user ID from token:', error);
    return null;
  }
};

/**
 * Get all user data from JWT token
 */
export const getUserDataFromToken = (token) => {
  try {
    const decoded = decodeJWT(token);
    if (!decoded) return null;
    
    return {
      id: decoded.id,
      username: decoded.username,
      email: decoded.email,
      role: decoded.role,
      permissions: decoded.permissions || [],
      exp: decoded.exp,
      iat: decoded.iat
    };
  } catch (error) {
    console.error('Error extracting user data from token:', error);
    return null;
  }
};
